import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from '../api/api';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPen,
    faTrash,
    faPlus,
    faSearch,
    faTimes,
    faTimesCircle,
    faEye,
    faSyncAlt,
    faSortAmountDown,
    faSortAmountUp
} from '@fortawesome/free-solid-svg-icons';

const STAGES = [
    "Qualification",
    "Approached",
    "Follow-up",
    "Meeting Scheduled",
    "Proposal Preparation",
    "Review/Discussion",
    "Completed"
];

const Opportunities = ({ onOpenDetail }) => {
    const navigate = useNavigate();
    const { empId, roleId, teamMemberIds, isSuper, isManager, canDelete, getRbacParams, activeOrgId, canWrite } = useAuth();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    const canEditItem = (item) => {
        if (!item) return false;
        if (isSuper) return true;
        if (isManager) {
            const teamIds = [empId, ...(teamMemberIds || []).map(Number)];
            return teamIds.includes(Number(item.Owner));
        }
        // Employee: only opportunity owner can edit (lead generator gets view-only)
        return Number(item.Owner) === Number(empId);
    };

    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [editMode, setEditMode] = useState(false);

    // Confirmation Modal State
    const [confirmModal, setConfirmModal] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: null
    });

    const [accounts, setAccounts] = useState([]);
    const [users, setUsers] = useState([]);

    const [showAccountDropdown, setShowAccountDropdown] = useState(false);
    const [showUserDropdown, setShowUserDropdown] = useState(false);

    const [accountSearch, setAccountSearch] = useState("");
    const [userSearch, setUserSearch] = useState("");

    // ================= FILTER STATES =================
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [stageFilter, setStageFilter] = useState("ALL");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [accountFilter, setAccountFilter] = useState("ALL");
    const [ownerFilter, setOwnerFilter] = useState("ALL");
    const [amountRange, setAmountRange] = useState("ALL");
    const [search, setSearch] = useState("");
    const [entries, setEntries] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState("OprID");
    const [sortOrder, setSortOrder] = useState("desc");

    // Modal Form State
    const [form, setForm] = useState({
        OprID: "",
        OpprName: "",
        Account: "",
        Amount: "",
        Stage: "Qualification",
        Owner: "",
        Status: "Open"
    });

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const rbac = getRbacParams();
            const qstr = new URLSearchParams(rbac).toString();
            const res = await api.get(`/opportunities?${qstr}`);
            setData(res.data || []);
        } catch (err) {
            console.error("Error loading opportunities:", err);
        } finally {
            setLoading(false);
        }
    }, [getRbacParams]);

    const fetchAccounts = useCallback(async () => {
        try {
            const rbac = getRbacParams();
            const qstr = new URLSearchParams(rbac).toString();
            const res = await api.get(`/leads?${qstr}`);
            setAccounts(res.data || []);
        } catch (err) {
            console.error("Error loading accounts:", err);
        }
    }, [getRbacParams]);

    const fetchUsers = useCallback(async () => {
        try {
            const rbac = getRbacParams();
            const qstr = new URLSearchParams(rbac).toString();
            const res = await api.get(`/users?${qstr}`);
            setUsers(res.data || []);
        } catch (err) {
            console.error("Error loading users:", err);
        }
    }, [getRbacParams]);

    useEffect(() => {
        fetchData();
        fetchAccounts();
        fetchUsers();
    }, [fetchData, fetchAccounts, fetchUsers, activeOrgId]);

    // Check if any filter is actively applied
    const isFiltered =
        startDate !== "" ||
        endDate !== "" ||
        stageFilter !== "ALL" ||
        statusFilter !== "ALL" ||
        accountFilter !== "ALL" ||
        ownerFilter !== "ALL" ||
        amountRange !== "ALL" ||
        search.trim().length > 0;

    const clearAllFilters = () => {
        setStartDate("");
        setEndDate("");
        setStageFilter("ALL");
        setStatusFilter("ALL");
        setAccountFilter("ALL");
        setOwnerFilter("ALL");
        setAmountRange("ALL");
        setSearch("");
        setCurrentPage(1);
    };

    // ================= FILTER LOGIC =================
    const filteredData = useMemo(() => {
        return data.filter(item => {
            // Date Filter (Start and End Date)
            if (item.CreatedAt) {
                const created = item.CreatedAt.slice(0, 10);
                if (startDate && created < startDate) return false;
                if (endDate && created > endDate) return false;
            }

            // Stage Filter
            if (stageFilter !== "ALL" && item.Stage !== stageFilter) {
                return false;
            }

            // Status Filter (Open, Win, Lose)
            if (statusFilter !== "ALL" && item.Status !== statusFilter) {
                return false;
            }

            // Account Filter
            if (accountFilter !== "ALL") {
                if (String(item.Account) !== String(accountFilter) && item.AccountName !== accountFilter) {
                    return false;
                }
            }

            // Owner Filter
            if (ownerFilter !== "ALL") {
                if (String(item.Owner) !== String(ownerFilter) && item.OwnerName !== ownerFilter) {
                    return false;
                }
            }

            // Amount Range Filter (USD)
            if (amountRange !== "ALL") {
                const amt = Number(item.Amount) || 0;
                if (amountRange === "UNDER_10K" && amt >= 10000) return false;
                if (amountRange === "10K_50K" && (amt < 10000 || amt > 50000)) return false;
                if (amountRange === "OVER_50K" && amt <= 50000) return false;
            }

            // Search Filter
            if (search.trim()) {
                const q = search.toLowerCase();
                const matchId = String(item.OprID).includes(q);
                const matchName = item.OpprName?.toLowerCase().includes(q);
                const matchAcc = item.AccountName?.toLowerCase().includes(q);
                const matchLeadGen = (String(item.AccountStatus || '').toLowerCase() === 'lead' ? item.LeadGeneratorName : '')?.toLowerCase().includes(q);
                const matchOwner = item.OwnerName?.toLowerCase().includes(q);
                const matchStage = item.Stage?.toLowerCase().includes(q);
                const matchStatus = item.Status?.toLowerCase().includes(q);
                const matchAmount = String(item.Amount || "").includes(q);

                if (!matchId && !matchName && !matchAcc && !matchLeadGen && !matchOwner && !matchStage && !matchStatus && !matchAmount) {
                    return false;
                }
            }

            return true;
        });
    }, [data, startDate, endDate, stageFilter, statusFilter, accountFilter, ownerFilter, amountRange, search]);

    // ================= SORT LOGIC =================
    const sortedData = useMemo(() => {
        if (!sortField) return filteredData;

        return [...filteredData].sort((a, b) => {
            let valA = a[sortField];
            let valB = b[sortField];

            // Numeric sort for Amount and ID
            if (sortField === "Amount" || sortField === "OprID") {
                valA = Number(valA) || 0;
                valB = Number(valB) || 0;
                return sortOrder === "asc" ? valA - valB : valB - valA;
            }

            valA = String(valA || "").toLowerCase();
            valB = String(valB || "").toLowerCase();

            if (valA < valB) return sortOrder === "asc" ? -1 : 1;
            if (valA > valB) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredData, sortField, sortOrder]);

    // ================= PAGINATION =================
    const totalPages = Math.ceil(sortedData.length / entries);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * entries;
        return sortedData.slice(start, start + entries);
    }, [sortedData, currentPage, entries]);

    // ================= SUMMARY METRICS (USD) =================
    const summaryMetrics = useMemo(() => {
        const totalPipeline = filteredData.reduce((sum, item) => sum + (Number(item.Amount) || 0), 0);
        const wonDeals = filteredData.filter(item => item.Status === "Win" || item.Stage === "Completed");
        const wonTotal = wonDeals.reduce((sum, item) => sum + (Number(item.Amount) || 0), 0);
        const avgDealSize = filteredData.length > 0 ? Math.round(totalPipeline / filteredData.length) : 0;
        const winRate = filteredData.length > 0 ? Math.round((wonDeals.length / filteredData.length) * 100) : 0;

        return {
            totalPipeline,
            count: filteredData.length,
            wonCount: wonDeals.length,
            wonTotal,
            avgDealSize,
            winRate
        };
    }, [filteredData]);

    // Unique Accounts & Owners for Filter Dropdowns
    const uniqueAccounts = useMemo(() => {
        const map = new Map();
        data.forEach(item => {
            if (item.Account && item.AccountName) {
                map.set(item.Account, item.AccountName);
            }
        });
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }, [data]);

    const uniqueOwners = useMemo(() => {
        const map = new Map();
        data.forEach(item => {
            if (item.Owner && item.OwnerName) {
                map.set(item.Owner, item.OwnerName);
            }
        });
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }, [data]);

    // Filter Users for Modal (strictly exclude anyone with higher RoleID)
    const assignableOpportunityOwners = useMemo(() => {
        return users.filter(u => {
            if (roleId && Number(u.RoleID) > Number(roleId)) return false;
            if (isSuper) return true;
            if (isManager) {
                return (
                    Number(u.ReportingTo) === Number(empId) ||
                    Number(u.empId) === Number(empId)
                );
            }
            return Number(u.empId) === Number(empId);
        });
    }, [users, roleId, empId, isSuper, isManager]);

    // Filtered lists for modal searchable dropdowns
    const filteredModalAccounts = useMemo(() => {
        if (!accountSearch.trim()) return accounts;
        const q = accountSearch.toLowerCase();
        return accounts.filter(a =>
            (a.AccountName || '').toLowerCase().includes(q) ||
            (a.City || '').toLowerCase().includes(q) ||
            (a.AccountType || '').toLowerCase().includes(q)
        );
    }, [accounts, accountSearch]);

    const filteredModalOwners = useMemo(() => {
        if (!userSearch.trim()) return assignableOpportunityOwners;
        const q = userSearch.toLowerCase();
        return assignableOpportunityOwners.filter(u =>
            (u.name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q) ||
            (u.designation || '').toLowerCase().includes(q)
        );
    }, [assignableOpportunityOwners, userSearch]);

    // ================= MODAL ACTIONS =================
    const openCreate = () => {
        setEditMode(false);
        const defaultOwner = empId || (assignableOpportunityOwners.length > 0 ? assignableOpportunityOwners[0].empId : '');
        const matchedOwner = assignableOpportunityOwners.find(u => Number(u.empId) === Number(defaultOwner)) || users.find(u => Number(u.empId) === Number(defaultOwner));

        setForm({
            OprID: "",
            OpprName: "",
            Account: "",
            Amount: "",
            Stage: "Qualification",
            Owner: defaultOwner,
            Status: "Open"
        });
        setAccountSearch("");
        setUserSearch(matchedOwner ? matchedOwner.name : "");
        setShowAccountDropdown(false);
        setShowUserDropdown(false);

        fetchAccounts();
        fetchUsers();
        setShowModal(true);
    };

    const handleEdit = (item) => {
        setEditMode(true);
        const accId = item.AccountID || item.Account || "";
        const matchedAcc = accounts.find(a => Number(a.AccountID) === Number(accId));
        const ownerId = item.Owner || empId || "";
        const matchedOwner = assignableOpportunityOwners.find(u => Number(u.empId) === Number(ownerId)) || users.find(u => Number(u.empId) === Number(ownerId));

        setForm({
            OprID: item.OprID,
            OpprName: item.OpprName || "",
            Account: accId,
            Amount: item.Amount || "",
            Stage: item.Stage || "Qualification",
            Owner: ownerId,
            Status: item.Status || "Open"
        });
        setAccountSearch(item.AccountName || (matchedAcc ? matchedAcc.AccountName : ""));
        setUserSearch(item.OwnerName || (matchedOwner ? matchedOwner.name : ""));
        setShowAccountDropdown(false);
        setShowUserDropdown(false);

        fetchAccounts();
        fetchUsers();
        setShowModal(true);
    };

    const openDelete = (id) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        try {
            await api.delete(`/opportunities/${deleteId}`);
            setData(prev => prev.filter(item => item.OprID !== deleteId));
            setShowDeleteModal(false);
            setDeleteId(null);
        } catch (err) {
            console.error("Delete failed:", err);
            alert("Failed to delete opportunity");
        }
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSavePrompt = () => {
        if (!form.OpprName.trim()) {
            alert('Opportunity Name is required');
            return;
        }

        setConfirmModal({
            open: true,
            title: editMode ? 'Confirm Opportunity Update' : 'Confirm Create Opportunity',
            message: editMode
                ? `Are you sure you want to save changes for "${form.OpprName}"?`
                : `Are you sure you want to create opportunity "${form.OpprName}" ($${Number(form.Amount || 0).toLocaleString()})?`,
            onConfirm: async () => {
                try {
                    const payload = {
                        ...form,
                        Owner: form.Owner || empId
                    };
                    if (editMode) {
                        await api.put(`/opportunities/${form.OprID}`, payload);
                    } else {
                        await api.post('/opportunities', payload);
                    }
                    setShowModal(false);
                    fetchData();
                } catch (err) {
                    console.error('Save error:', err);
                    alert('Failed to save opportunity');
                }
            }
        });
    };

    return (
        <Layout>
            <div className="dome-dashboard-wrap">

                {/* ================= 1. TOP HEADER ================= */}
                <div className="dome-dash-header-row">
                    <div className="dome-dash-title-wrap">
                        <h1 className="dome-dash-h1">Opportunities</h1>
                        <span className="dome-dash-badge">{filteredData.length} active deals</span>
                    </div>

                    <div className="dome-dash-actions">
                        <button
                            type="button"
                            className="btn-dome-secondary"
                            title="Refresh opportunities directory"
                            onClick={fetchData}
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
                            <span>Refresh</span>
                        </button>

                        <button
                            type="button"
                            className={`btn-dome-new ${canWrite ? '' : 'btn-action-disabled'}`}
                            onClick={openCreate}
                            disabled={!canWrite}
                            title={!canWrite ? 'Select a specific organization to create opportunities (disabled in All Organizations mode)' : 'Create New Opportunity'}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            <span>New Opportunity</span>
                        </button>
                    </div>
                </div>

                {/* ================= 2. 4-CARD KPI RIBBON (USD) ================= */}
                <div className="dome-kpi-strip">
                    <div className="dome-kpi-card">
                        <span className="dome-kpi-label">Filtered Pipeline</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">${summaryMetrics.totalPipeline.toLocaleString("en-US")}</span>
                        </div>
                        <span className="dome-kpi-sub">{summaryMetrics.count} active deals</span>
                    </div>

                    <div
                        className={`dome-kpi-card cursor-pointer ${statusFilter === 'Win' ? 'active-filter-card' : ''}`}
                        onClick={() => {
                            setStatusFilter(prev => prev === 'Win' ? 'ALL' : 'Win');
                            setCurrentPage(1);
                        }}
                        title="Filter Won Deals"
                    >
                        <span className="dome-kpi-label">Won / Completed Deals</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num text-success">${summaryMetrics.wonTotal.toLocaleString("en-US")}</span>
                        </div>
                        <span className="dome-kpi-sub">{summaryMetrics.wonCount} won opportunities</span>
                    </div>

                    <div className="dome-kpi-card">
                        <span className="dome-kpi-label">Average Deal Size</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">${summaryMetrics.avgDealSize.toLocaleString("en-US")}</span>
                        </div>
                        <span className="dome-kpi-sub">Across filtered selection</span>
                    </div>

                    <div className="dome-kpi-card">
                        <span className="dome-kpi-label">Win Rate</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num text-primary">{summaryMetrics.winRate}%</span>
                        </div>
                        <span className="dome-kpi-sub">{summaryMetrics.wonCount} of {summaryMetrics.count} deals converted</span>
                    </div>
                </div>

                {/* ================= 3. COMPACT SUBNAV FILTER STRIP ================= */}
                <div className="dome-dash-subnav-strip dome-opp-subnav">
                    <div className="dome-opp-filters-row">
                        {/* Stage Filter */}
                        <div className="dome-stacked-filter-item">
                            <label className="dome-stacked-filter-label">Stage</label>
                            <select
                                value={stageFilter}
                                onChange={(e) => { setStageFilter(e.target.value); setCurrentPage(1); }}
                                className="dome-stacked-select"
                            >
                                <option value="ALL">All Stages ({data.length})</option>
                                {STAGES.map(stg => {
                                    const count = data.filter(d => d.Stage === stg).length;
                                    return (
                                        <option key={stg} value={stg}>
                                            {stg} ({count})
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        {/* Status Filter */}
                        <div className="dome-stacked-filter-item">
                            <label className="dome-stacked-filter-label">Status</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                                className="dome-stacked-select"
                            >
                                <option value="ALL">All Statuses</option>
                                <option value="Open">Open</option>
                                <option value="Win">Win</option>
                                <option value="Lose">Lose</option>
                            </select>
                        </div>

                        {/* Account Filter */}
                        <div className="dome-stacked-filter-item">
                            <label className="dome-stacked-filter-label">Account</label>
                            <select
                                value={accountFilter}
                                onChange={(e) => { setAccountFilter(e.target.value); setCurrentPage(1); }}
                                className="dome-stacked-select"
                            >
                                <option value="ALL">All Accounts</option>
                                {uniqueAccounts.map(acc => (
                                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Owner Filter */}
                        <div className="dome-stacked-filter-item">
                            <label className="dome-stacked-filter-label">Owner</label>
                            <select
                                value={ownerFilter}
                                onChange={(e) => { setOwnerFilter(e.target.value); setCurrentPage(1); }}
                                className="dome-stacked-select"
                            >
                                <option value="ALL">All Owners</option>
                                {uniqueOwners.map(u => (
                                    <option key={u.id} value={u.id}>{u.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Amount Range Filter */}
                        <div className="dome-stacked-filter-item">
                            <label className="dome-stacked-filter-label">Amount</label>
                            <select
                                value={amountRange}
                                onChange={(e) => { setAmountRange(e.target.value); setCurrentPage(1); }}
                                className="dome-stacked-select"
                            >
                                <option value="ALL">All Amounts</option>
                                <option value="UNDER_10K">&lt; $10,000</option>
                                <option value="10K_50K">$10,000 - $50,000</option>
                                <option value="OVER_50K">&gt; $50,000</option>
                            </select>
                        </div>

                        {/* Date Filter */}
                        <div className="dome-stacked-filter-item dome-stacked-date-item">
                            <label className="dome-stacked-filter-label">Date Range</label>
                            <div className="dome-stacked-date-inputs">
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                                    className="dome-stacked-date-input"
                                    title="Start Date"
                                />
                                <span className="dome-date-sep">&ndash;</span>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                                    className="dome-stacked-date-input"
                                    title="End Date"
                                />
                            </div>
                        </div>

                        {/* Clear Filters Button */}
                        {isFiltered && (
                            <div className="dome-stacked-filter-action">
                                <button
                                    type="button"
                                    className="dome-clear-filters-mini"
                                    onClick={clearAllFilters}
                                    title="Reset all active filters"
                                >
                                    <FontAwesomeIcon icon={faTimesCircle} />
                                    <span>Reset</span>
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="dome-opp-search-row">
                        <div className="dome-search-full-box">
                            <FontAwesomeIcon icon={faSearch} className="dome-search-full-icon" />
                            <input
                                type="text"
                                placeholder="Search opportunities, accounts, owners..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="dome-search-full-input"
                            />
                            {search && (
                                <button
                                    type="button"
                                    className="dome-search-clear-mini"
                                    onClick={() => { setSearch(''); setCurrentPage(1); }}
                                    title="Clear search"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* ================= 4. TABLE CARD ================= */}
                <div className="dome-dash-card">
                    <div className="dome-dash-card-header">
                        <h3 className="dome-dash-card-title">Opportunities Pipeline</h3>

                        <div className="dome-table-header-controls">
                            <div className="dome-mini-filter-item">
                                <span className="dome-filter-mini-label">Sort:</span>
                                <select
                                    value={sortField}
                                    onChange={(e) => setSortField(e.target.value)}
                                    className="dome-mini-select"
                                >
                                    <option value="OprID">ID</option>
                                    <option value="OpprName">Name</option>
                                    <option value="AccountName">Account</option>
                                    <option value="LeadGeneratorName">Lead Generator</option>
                                    <option value="Amount">Amount</option>
                                    <option value="Stage">Stage</option>
                                    <option value="Status">Status</option>
                                </select>
                                <button
                                    type="button"
                                    className="dome-sort-dir-mini"
                                    onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
                                    title={`Sort Order: ${sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}
                                >
                                    <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountDown : faSortAmountUp} />
                                </button>
                            </div>

                            <div className="dome-mini-filter-item">
                                <span className="dome-filter-mini-label">Show:</span>
                                <select
                                    value={entries}
                                    onChange={(e) => {
                                        setEntries(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="dome-mini-select"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="dome-dash-table-wrap">
                        <table className="dome-dash-table dome-opp-table">
                            <thead>
                                <tr>
                                    <th className="th-id">ID</th>
                                    <th>Opportunity Name</th>
                                    <th>Account / Client</th>
                                    <th className="th-amount">Amount (USD)</th>
                                    <th className="th-stage">Stage</th>
                                    <th>Lead Generator</th>
                                    <th>Opportunity Owner</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="9" className="td-empty text-center">
                                            Loading opportunities...
                                        </td>
                                    </tr>
                                ) : paginatedData.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="td-empty text-center">
                                            <div>No opportunities match your filter criteria.</div>
                                            {isFiltered && (
                                                <button
                                                    type="button"
                                                    className="btn-dome-secondary mt-8"
                                                    onClick={clearAllFilters}
                                                >
                                                    Reset Filters
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedData.map(item => (
                                        <tr
                                            key={item.OprID}
                                            onClick={() => onOpenDetail ? onOpenDetail(item.OprID) : navigate(`/opportunities/${item.OprID}`)}
                                            className="cursor-pointer"
                                        >
                                            <td>
                                                <span className="dome-id-pill">
                                                    {item.OprID}</span>
                                            </td>

                                            <td>
                                                <span className="dome-opp-name-link">
                                                    {item.OpprName}
                                                </span>
                                            </td>

                                            <td>
                                                {item.AccountName || "Unassigned"}
                                            </td>

                                            <td>
                                                ${Number(item.Amount || 0).toLocaleString("en-US")}
                                            </td>

                                            <td>
                                                {item.Stage || "Qualification"}
                                            </td>

                                            <td>
                                                {String(item.AccountStatus || '').toLowerCase() === 'lead' && item.LeadGeneratorName
                                                    ? item.LeadGeneratorName
                                                    : "—"}
                                            </td>

                                            <td>
                                                {item.OwnerName || "System Admin"}
                                            </td>

                                            <td>
                                                {item.Status || "Open"}
                                            </td>

                                            <td className="text-right">
                                                <div className="dome-row-actions" onClick={(e) => e.stopPropagation()}>
                                                    {canEditItem(item) ? (
                                                        <button
                                                            type="button"
                                                            className="dome-icon-btn"
                                                            title="Edit Opportunity"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEdit(item);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faPen} />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="dome-icon-btn"
                                                            title="View-Only: You are Lead Generator (Only Opportunity Owner can edit)"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (onOpenDetail) onOpenDetail(item.OprID);
                                                                else navigate(`/opportunities/${item.OprID}`);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faEye} />
                                                        </button>
                                                    )}

                                                    {canDelete && canEditItem(item) && (
                                                        <button
                                                            type="button"
                                                            className="dome-icon-btn dome-icon-btn-danger"
                                                            title="Delete Opportunity"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openDelete(item.OprID);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ================= 5. TABLE FOOTER & PAGINATION ================= */}
                    <div className="dome-table-footer">
                        <div className="dome-table-footer-info">
                            Showing {sortedData.length > 0 ? (currentPage - 1) * entries + 1 : 0} to {Math.min(currentPage * entries, sortedData.length)} of {sortedData.length} entries
                            {isFiltered && ` (filtered from ${data.length} total)`}
                        </div>

                        <div className="dome-pagination-controls">
                            <button
                                type="button"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                                className="dome-page-btn"
                            >
                                Prev
                            </button>

                            <span className="dome-page-indicator">
                                Page {currentPage} of {totalPages || 1}
                            </span>

                            <button
                                type="button"
                                disabled={currentPage === totalPages || totalPages === 0}
                                onClick={() => setCurrentPage(p => p + 1)}
                                className="dome-page-btn"
                            >
                                Next
                            </button>
                        </div>
                    </div>

                </div>

                {/* ================= CREATE / EDIT MODAL ================= */}
                {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="modal dome-modal-card" onClick={(e) => e.stopPropagation()}>

                            <div className="modal-header modal-header-flex">
                                <h3>{editMode ? `Edit Opportunity (${form.OprID})` : "New Opportunity"}</h3>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="modal-btn-close"
                                    title="Close Modal"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>

                            <div
                                className="modal-body"
                                onClick={(e) => {
                                    if (!e.target.closest('.pos-relative')) {
                                        setShowAccountDropdown(false);
                                        setShowUserDropdown(false);
                                    }
                                }}
                            >
                                <div className="form-grid-3col">

                                    {/* Opportunity Name */}
                                    <div className="input-group input-group-span-2">
                                        <label>Opportunity Name <span className="text-danger">*</span></label>
                                        <input
                                            name="OpprName"
                                            value={form.OpprName}
                                            onChange={handleChange}
                                            placeholder="e.g. Enterprise Cloud Deployment"
                                            required
                                        />
                                    </div>

                                    {/* Amount (USD) */}
                                    <div className="input-group">
                                        <label>Amount (USD $) <span className="text-danger">*</span></label>
                                        <input
                                            name="Amount"
                                            type="number"
                                            value={form.Amount}
                                            onChange={handleChange}
                                            placeholder="e.g. 50000"
                                            required
                                        />
                                    </div>

                                    {/* Searchable Account Dropdown */}
                                    <div className={`input-group input-group-span-2 pos-relative ${showAccountDropdown ? 'dropdown-active-z' : ''}`}>
                                        <label>Account / Client <span className="text-danger">*</span></label>
                                        <div className="searchable-select-wrap">
                                            <input
                                                type="text"
                                                placeholder="Search account by name, type, or city..."
                                                value={accountSearch}
                                                onChange={(e) => {
                                                    setAccountSearch(e.target.value);
                                                    setShowAccountDropdown(true);
                                                    if (!e.target.value) setForm(f => ({ ...f, Account: '' }));
                                                }}
                                                onFocus={() => {
                                                    setShowAccountDropdown(true);
                                                    setShowUserDropdown(false);
                                                }}
                                                className="searchable-input"
                                            />
                                            {form.Account && (
                                                <button
                                                    type="button"
                                                    className="clear-select-btn"
                                                    onClick={() => {
                                                        setForm(f => ({ ...f, Account: '' }));
                                                        setAccountSearch('');
                                                        setShowAccountDropdown(true);
                                                    }}
                                                    title="Clear selection"
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </button>
                                            )}
                                        </div>

                                        {showAccountDropdown && (
                                            <div className="searchable-dropdown-popover">
                                                {filteredModalAccounts.length > 0 ? (
                                                    filteredModalAccounts.map(acc => (
                                                        <div
                                                            key={acc.AccountID}
                                                            className={`searchable-dropdown-item ${String(form.Account) === String(acc.AccountID) ? 'selected' : ''}`}
                                                            onClick={() => {
                                                                setForm(f => ({ ...f, Account: acc.AccountID }));
                                                                setAccountSearch(acc.AccountName);
                                                                setShowAccountDropdown(false);
                                                            }}
                                                        >
                                                            <div className="item-main">
                                                                <span className="item-title">{acc.AccountName}</span>
                                                                <span className="item-badge">{acc.AccountType || 'Organization'}</span>
                                                            </div>
                                                            {acc.City && <span className="item-sub">📍 {acc.City}</span>}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="searchable-dropdown-empty">
                                                        No accounts match "{accountSearch}"
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Sales Stage (7 Stages) */}
                                    <div className="input-group">
                                        <label>Sales Stage <span className="text-danger">*</span></label>
                                        <select
                                            name="Stage"
                                            value={form.Stage}
                                            onChange={handleChange}
                                            required
                                        >
                                            {STAGES.map(stg => (
                                                <option key={stg} value={stg}>{stg}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Searchable Opportunity Owner Dropdown */}
                                    <div className={`input-group input-group-span-2 pos-relative ${showUserDropdown ? 'dropdown-active-z' : ''}`}>
                                        <label>Opportunity Owner <span className="text-danger">*</span></label>
                                        <div className="searchable-select-wrap">
                                            <input
                                                type="text"
                                                placeholder="Search owner by name, email, or designation..."
                                                value={userSearch}
                                                onChange={(e) => {
                                                    setUserSearch(e.target.value);
                                                    setShowUserDropdown(true);
                                                    if (!e.target.value) setForm(f => ({ ...f, Owner: '' }));
                                                }}
                                                onFocus={() => {
                                                    setShowUserDropdown(true);
                                                    setShowAccountDropdown(false);
                                                }}
                                                className="searchable-input"
                                            />
                                            {form.Owner && (
                                                <button
                                                    type="button"
                                                    className="clear-select-btn"
                                                    onClick={() => {
                                                        setForm(f => ({ ...f, Owner: '' }));
                                                        setUserSearch('');
                                                        setShowUserDropdown(true);
                                                    }}
                                                    title="Clear selection"
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </button>
                                            )}
                                        </div>

                                        {showUserDropdown && (
                                            <div className="searchable-dropdown-popover">
                                                {filteredModalOwners.length > 0 ? (
                                                    filteredModalOwners.map(u => (
                                                        <div
                                                            key={u.empId}
                                                            className={`searchable-dropdown-item ${String(form.Owner) === String(u.empId) ? 'selected' : ''}`}
                                                            onClick={() => {
                                                                setForm(f => ({ ...f, Owner: u.empId }));
                                                                setUserSearch(u.name);
                                                                setShowUserDropdown(false);
                                                            }}
                                                        >
                                                            <div className="item-main">
                                                                <span className="item-title">{u.name}</span>
                                                                <span className="item-badge">{u.designation || 'Representative'}</span>
                                                            </div>
                                                            <span className="item-sub">✉️ {u.email}</span>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="searchable-dropdown-empty">
                                                        No team members match "{userSearch}"
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Status (Open, Win, Lose) */}
                                    <div className="input-group">
                                        <label>Status</label>
                                        <select
                                            name="Status"
                                            value={form.Status}
                                            onChange={handleChange}
                                        >
                                            <option value="Open">Open (Active Deal)</option>
                                            <option value="Win">Win (Closed Won)</option>
                                            <option value="Lose">Lose (Closed Lost)</option>
                                        </select>
                                    </div>

                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn-dome-secondary"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    className="btn-dome-new"
                                    onClick={handleSavePrompt}
                                >
                                    {editMode ? "Save Changes" : "Create Opportunity"}
                                </button>
                            </div>

                        </div>
                    </div>
                )}

                {/* ================= CONFIRMATION MODAL ================= */}
                {confirmModal.open && (
                    <div className="modal-overlay">
                        <div className="modal modal-confirm-sm">
                            <div className="modal-header">
                                <h3>{confirmModal.title}</h3>
                            </div>
                            <div className="modal-body">
                                <p className="confirm-modal-text">
                                    {confirmModal.message}
                                </p>
                                <div className="modal-footer mt-20">
                                    <button
                                        type="button"
                                        className="btn-dome-secondary"
                                        onClick={() => setConfirmModal({ open: false, title: '', message: '', onConfirm: null })}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-dome-new"
                                        onClick={() => {
                                            if (typeof confirmModal.onConfirm === 'function') {
                                                confirmModal.onConfirm();
                                            }
                                            setConfirmModal({ open: false, title: '', message: '', onConfirm: null });
                                        }}
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ================= DELETE CONFIRMATION MODAL ================= */}
                {showDeleteModal && (
                    <div className="modal-overlay">
                        <div className="modal modal-confirm-sm">
                            <div className="modal-header">
                                <h3>Confirm Delete</h3>
                            </div>

                            <div className="modal-body">
                                <p className="confirm-modal-text">
                                    Are you sure you want to delete this opportunity record? This action cannot be undone.
                                </p>

                                <div className="modal-footer mt-20">
                                    <button
                                        type="button"
                                        className="btn-dome-secondary"
                                        onClick={() => setShowDeleteModal(false)}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="button"
                                        className="btn-dome-new dome-btn-danger"
                                        onClick={handleDelete}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </Layout>
    );
};

export default Opportunities;